import { apiClient } from '../client';
import { API_ENDPOINTS } from '../constant/endpoints';
import type { IApiResponse } from '../types';

import type {
    IOnboardingStatus,
    IOnboardingWizard,
    ITestLeadTrace,
    IWizardAiPolicyPayload,
    IWizardBusinessPayload,
    IWizardGoLive,
    IWizardLeadProfilePayload,
    IWizardPlaybook,
    LeadSourceChoice,
} from '@/features/onboarding/types';

export const onboardingApi = {
    status: () =>
        apiClient
            .get<IApiResponse<IOnboardingStatus>>(API_ENDPOINTS.ONBOARDING.STATUS)
            .then((r) => r.data.data),

    welcomeSeen: () =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.WELCOME_SEEN)
            .then(() => undefined),

    skip: () =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.SKIP)
            .then(() => undefined),

    dismissRail: () =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.DISMISS_RAIL)
            .then(() => undefined),

    celebrate: () =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.CELEBRATE)
            .then(() => undefined),

    wizard: () =>
        apiClient
            .get<IApiResponse<IOnboardingWizard>>(API_ENDPOINTS.ONBOARDING.WIZARD)
            .then((r) => r.data.data),

    /** Legacy business step — kept for organizations still on the old flow. */
    saveWizardBusiness: (payload: IWizardBusinessPayload) =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.WIZARD_BUSINESS, payload)
            .then(() => undefined),

    /** Step 1 — business basics, service area, hours, after-hours, booking. */
    saveLeadProfile: (payload: IWizardLeadProfilePayload) =>
        apiClient
            .post<
                IApiResponse<{ ok: boolean }>
            >(API_ENDPOINTS.ONBOARDING.WIZARD_LEAD_PROFILE, payload)
            .then(() => undefined),

    /** Step 2 — where the org's leads come in. */
    saveLeadSource: (choice: LeadSourceChoice) =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.WIZARD_LEAD_SOURCE, {
                choice,
            })
            .then(() => undefined),

    /**
     * Step 3 — installs the Home-Service Lead Response playbook. The response's
     * `pending` items are the ONLY place they appear; a later wizard read
     * always reports `pending: []`.
     */
    installPlaybook: () =>
        apiClient
            .post<IApiResponse<IWizardPlaybook>>(API_ENDPOINTS.ONBOARDING.WIZARD_PLAYBOOK)
            .then((r) => r.data.data),

    /** Step 4 — the AI reply policy sign-off. */
    saveAiPolicy: (payload: IWizardAiPolicyPayload) =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.WIZARD_AI_POLICY, payload)
            .then(() => undefined),

    /** Step 5 — creates (or reuses) the org's one sample lead. */
    runTestLead: () =>
        apiClient
            .post<IApiResponse<{ contactId: string }>>(API_ENDPOINTS.ONBOARDING.TEST_LEAD)
            .then((r) => r.data.data),

    /** Step 5 — polled until `complete`; also what delivers the sample reply. */
    testLeadTrace: (contactId: string) =>
        apiClient
            .get<IApiResponse<ITestLeadTrace>>(API_ENDPOINTS.ONBOARDING.TEST_LEAD_TRACE(contactId))
            .then((r) => r.data.data),

    /**
     * Step 6. Returns the readiness either way: when a blocker remains nothing
     * is stamped and `nextAction` says what is missing.
     */
    goLive: () =>
        apiClient
            .post<IApiResponse<IWizardGoLive>>(API_ENDPOINTS.ONBOARDING.WIZARD_GO_LIVE)
            .then((r) => r.data.data),

    advanceWizard: (step: number) =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.WIZARD_ADVANCE, { step })
            .then(() => undefined),

    completeWizard: () =>
        apiClient
            .post<IApiResponse<{ ok: boolean }>>(API_ENDPOINTS.ONBOARDING.WIZARD_COMPLETE)
            .then(() => undefined),
};
